import React from "react";
import {
  X,
  ShieldCheck,
  Calendar,
  Clock,
  Hash,
  FileText,
  Activity,
} from "lucide-react";

const ViewMandate = ({ data, setMandateData }) => {
  if (!data) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusStyles = {
    PENDING: "bg-yellow-100 text-yellow-700",
    ACTIVATED: "bg-green-100 text-green-700",
    active: "bg-green-100 text-green-700",
    CREATED: "bg-green-100 text-green-700",
    FINISHED: "bg-blue-100 text-blue-700",
    FAILED: "bg-red-100 text-red-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Glassmorphism Overlay */}
      <div
        onClick={() => setMandateData(null)}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-md transition-opacity"
      />

      {/* Modal Card */}
      <div className="relative max-h-[90%] overflow-y-auto  bg-white w-full max-w-xl md:max-w-4xl lg:max-w-6xl rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header - Gradient Accent */}
        <div className="relative h-6 bg-gradient-to-r from-rose-600 to-blue-600 w-full" />

        <div className="px-8 py-6 flex justify-between items-center border-b border-slate-100 bg-slate-50/30">
          {/* Left: Identity Group */}

          <div className="flex flex-col items-start gap-2">
            <h2 className="text-xl font-bold flex flex-col gap-2 text-slate-700 tracking-tight leading-tight">
              {" "}
              Mandate Details of -
            </h2>
            <div className="flex gap-2">
              <div className="relative">
                <div className="p-3 bg-white rounded-2xl shadow-sm border border-blue-100 text-blue-600">
                  <ShieldCheck size={26} strokeWidth={2.5} />
                </div>
                {/* Decorative pulse dot */}
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                </span>
              </div>

              <div>
                <h2 className="text-xl font-bold flex flex-col gap-2 text-slate-700 tracking-tight leading-tight">
                  <span className="tracking-wider p-3 rounded-lg bg-gradient-to-r from-green-50 to-green-200 font-extrabold text-green-600 italic text-2xl">
                    {data?.name}
                  </span>
                </h2>
              </div>
            </div>
          </div>

          {/* Right: Action & Context Group */}
          <div className="flex items-center gap-6">
            {/* Payment Mode Indicator */}
            <div className="flex flex-col items-end gap-1.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Payment Mode
              </span>
              <div className="flex items-center justify-center h-10 px-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-blue-200 transition-colors cursor-default group">
                {data.mode?.startsWith("UPI") ? (
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg"
                    alt="UPI"
                    className="h-4 w-auto grayscale group-hover:grayscale-0 transition-all"
                  />
                ) : (
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/4/46/NACH_logo.svg"
                    alt="NACH"
                    className="h-5 w-auto"
                  />
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="h-10 w-[1px] bg-slate-200 hidden sm:block" />

            {/* Close Button */}
            <button
              onClick={() => setMandateData(null)}
              className="group relative p-2.5 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 rounded-xl text-slate-400 hover:text-rose-600 shadow-sm transition-all active:scale-90"
              title="Close Details"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-8 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-8">
            {/* ================= MANDATE INFO ================= */}
            <DataField
              label="Mandate ID"
              value={data?.mandate_id}
              icon={<Hash size={14} />}
            />
            <DataField
              label="Reference ID"
              value={data?.reference_id}
              icon={<Hash size={14} />}
            />
            <DataField
              label="Type"
              value={data?.type}
              icon={<ShieldCheck size={14} />}
            />
            <DataField
              label="Mode"
              value={data?.mode}
              icon={<Activity size={14} />}
            />

            {/* STATUS */}
            <div className="space-y-1 ">
              <p className="flex items-center mb-2 text-xs font-semibold text-slate-400 uppercase">
                <Activity size={14} /> Status
              </p>
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${
                  statusStyles[data.status] || "bg-gray-100 text-gray-600"
                }`}
              >
                {data?.status}
              </span>
            </div>

            <DataField
              label="Frequency"
              value={data?.frequency}
              icon={<Clock size={14} />}
            />
            <DataField
              label="Installments"
              value={data?.installment_count}
              icon={<Hash size={14} />}
            />

            {/* ================= PAYMENT ================= */}
            <DataField label="Amount" value={`₹ ${data?.amount}`} />
            <DataField
              label="Approval Amount"
              value={`₹ ${data?.approval_amount}`}
            />
            <DataField
              label="Advance Amount"
              value={`₹ ${data?.advance_amount}`}
            />

            <DataField
              label="Start Date"
              value={formatDate(data?.start_date)}
              icon={<Calendar size={14} />}
            />
            <DataField
              label="End Date"
              value={formatDate(data?.end_date)}
              icon={<Calendar size={14} />}
            />

            {/* ================= USER ================= */}
            <DataField label="Customer Name" value={data?.name} />
            <DataField label="Customer Mobile" value={data?.mobile_number} />

            <DataField label="User Name" value={data?.user?.name} />
            <DataField label="User Mobile" value={data?.user?.mobile_number} />

            {/* ================= COMPANY ================= */}
            <div className="sm:col-span-2 p-4 bg-blue-100 rounded-xl border">
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">
                Company Details
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DataField
                  label="Company Name"
                  value={data?.user?.company_details?.company_name}
                />
                <DataField
                  label="PAN"
                  value={data?.user?.company_details?.company_pan}
                />
                <DataField
                  label="GST"
                  value={data?.user?.company_details?.company_gst_no}
                />
                <DataField
                  label="CIN"
                  value={data?.user?.company_details?.company_cin}
                />
                <DataField
                  label="Business Type"
                  value={data?.user?.company_details?.line_of_business}
                />
              </div>
            </div>

            {/* ================= DESCRIPTION ================= */}
            <div className="sm:col-span-2 p-4 bg-blue-100 rounded-xl border">
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">
                Description
              </p>
              <p className="text-sm text-slate-700">
                {data?.description || "—"}
              </p>
            </div>

            {/* ================= META ================= */}
            <DataField
              label="Created At"
              value={formatDate(data?.created_at)}
              icon={<Calendar size={14} />}
            />
            <DataField
              label="Updated At"
              value={formatDate(data?.updated_at)}
              icon={<Clock size={14} />}
            />

            {/* INTERNAL ID */}
            <div className="sm:col-span-2">
              <p className="text-[10px] font-bold text-slate-300 uppercase mb-1">
                Internal ID
              </p>
              <code className="text-[11px] bg-slate-100 px-2 py-1 rounded text-slate-500 font-mono break-all block">
                {data?.id}
              </code>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-slate-50/50 border-t border-slate-100 flex justify-end">
          <button
            onClick={() => setMandateData(null)}
            className="px-5 py-2.5 bg-rose-600 border border-slate-200 text-slate-100 font-semibold rounded-xl hover:bg-green-600 hover:border-green-300 shadow-sm transition-all active:scale-95"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable Sub-component for clean code
const DataField = ({ label, value, icon, isCritical = false }) => (
  <div className="space-y-1">
    <p className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-tight">
      {icon} {label}
    </p>
    <p
      className={`text-[15px] font-semibold ${isCritical ? "text-rose-600" : "text-slate-800"}`}
    >
      {value || "—"}
    </p>
  </div>
);

export default ViewMandate;
